<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { nextTick, ref } from 'vue'

import { editStudentSubmission } from '@/composables/current-student-advanced-standing-form/useStudentFormApi'
import type { StudentEnrolmentStatus } from '@/models/current-student-advanced-standing-form/api'
import { useCurrentStudentApplicationStore } from '@/stores/current-student-advanced-standing-form/applicationStore'
import { useCurrentStudentCourseSelectionStore } from '@/stores/current-student-advanced-standing-form/courseSelectionStore'
import LaTrobeCourseSelectionStep from './LaTrobeCourseSelectionStep.vue'

const props = defineProps<{
  handleAuthSessionError: (error: unknown, behavior: 'redirect' | 'reload') => boolean
  getErrorMessage: (error: unknown) => string
}>()

const emit = defineEmits<{
  'course-saved': []
}>()

const applicationStore = useCurrentStudentApplicationStore()
const courseSelectionStore = useCurrentStudentCourseSelectionStore()
const {
  secureData,
} = storeToRefs(applicationStore)
const {
  selectedCourseId,
  enrolmentStatus,
  sponsoredStudent,
  invalidCourseMessage,
  invalidEnrolmentStatusMessage,
  selectableCourses,
  isInternationalCourse,
  isSponsoredStudentLocked,
  isSponsoredStudent,
  handbookUrl,
  showSpgTool,
  courseSelectClass,
  enrolmentStatusClass,
  internationalStudentClass,
} = storeToRefs(courseSelectionStore)

const coursePageMessage = ref('')
const coursePageMessageIsError = ref(false)
const courseSaving = ref(false)

function onCourseChange(): void {
  coursePageMessage.value = ''
  courseSelectionStore.onCourseChange()
}

function onEnrolmentStatusChange(): void {
  courseSelectionStore.onEnrolmentStatusChange()
}

function onSponsoredStudentChange(): void {
  courseSelectionStore.onSponsoredStudentChange()
}

async function validateAndSaveCourseSelection(): Promise<void> {
  if (courseSaving.value) return
  if (!validateCourseInput() || !validateEnrolmentStatus() || !validateInternationalUnder18Input()) return

  courseSaving.value = true
  coursePageMessage.value = 'Saving...'
  coursePageMessageIsError.value = false

  try {
    const result = await editStudentSubmission({
      secure_data: secureData.value,
      student_course_id: selectedCourseId.value,
      student_enrolment_status: enrolmentStatus.value as StudentEnrolmentStatus,
      sponsored_student: sponsoredStudent.value === '1',
    })
    applicationStore.setSubmission(result.data.submission)
    coursePageMessage.value = ''
    emit('course-saved')
  } catch (error) {
    if (props.handleAuthSessionError(error, 'reload')) return

    coursePageMessage.value = props.getErrorMessage(error)
    coursePageMessageIsError.value = true
    scrollToElementAfterRender('#coursePageError')
  } finally {
    courseSaving.value = false
  }
}

function validateCourseInput(): boolean {
  return courseSelectionStore.validateCourseInput()
}

function validateEnrolmentStatus(): boolean {
  if (!courseSelectionStore.validateEnrolmentStatus()) {
    scrollValidationTarget('input[name="studentEnrolmentStatus"]')
    return false
  }

  return true
}

function validateInternationalUnder18Input(): boolean {
  if (!courseSelectionStore.validateInternationalUnder18Input()) {
    scrollValidationTarget('#InternationalStudentUnder18Input')
    return false
  }

  return true
}

function scrollValidationTarget(selector: string): void {
  document.querySelector(selector)?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'nearest',
  })
}

function scrollToElementAfterRender(selector: string): void {
  void nextTick(() => {
    window.requestAnimationFrame(() => {
      document.querySelector(selector)?.scrollIntoView()
    })
  })
}

defineExpose({
  validateAndSaveCourseSelection,
})
</script>

<template>
  <LaTrobeCourseSelectionStep
    v-model:selected-course-id="selectedCourseId"
    v-model:enrolment-status="enrolmentStatus"
    v-model:sponsored-student="sponsoredStudent"
    :selectable-courses="selectableCourses"
    :course-select-class="courseSelectClass"
    :invalid-course-message="invalidCourseMessage"
    :enrolment-status-class="enrolmentStatusClass"
    :invalid-enrolment-status-message="invalidEnrolmentStatusMessage"
    :handbook-url="handbookUrl"
    :show-spg-tool="showSpgTool"
    :is-international-course="isInternationalCourse"
    :is-sponsored-student-locked="isSponsoredStudentLocked"
    :is-sponsored-student="isSponsoredStudent"
    :international-student-class="internationalStudentClass"
    :course-page-message="coursePageMessage"
    :course-page-message-is-error="coursePageMessageIsError"
    @course-change="onCourseChange"
    @enrolment-status-change="onEnrolmentStatusChange"
    @sponsored-student-change="onSponsoredStudentChange"
  />
</template>
